import { Logger } from './logger';

const logger = Logger.createScoped('JwtRenewalCoordinator');
const LockNamespace = 'zs:auth:jwt-renewal';
const StorageLeasePrefix = 'zs:auth:jwtRenewalLock:';
const LeaseMs = 30_000;
const LeaseSettleMs = 75;
const LeaseWaitBufferMs = 500;
const MaxLeaseAttempts = 3;
const FailedAttemptCooldownMs = 15_000;

interface WebLockManager {
    request<T>(name: string, options: { mode: 'exclusive' }, callback: () => T | Promise<T>): Promise<T>;
}
interface JwtRenewalLease {
    ownerId: string;
    expiresAt: number;
}
export type JwtRenewalResult = { status: 'renewed' | 'cleared' | 'failed' };
interface RenewalOptions {
    scope: string;
    /** Browser session mutex; credentials and failure state stay in this tab. */
    lockScope: string;
    shouldRenew: () => boolean;
    performRenewal: () => Promise<JwtRenewalResult>;
}
function delay(ms: number) {
    return new Promise(resolve => window.setTimeout(resolve, ms));
}

class JwtRenewalCoordinatorService {
    readonly ownerId = typeof crypto !== 'undefined' && 'randomUUID' in crypto ? crypto.randomUUID() : `${Date.now()}:${Math.random()}`;
    private failedAttempt: { scope: string; until: number } | null = null;

    async renew(options: RenewalOptions) {
        if (!this.shouldAttempt(options)) return;
        const lockManager = (navigator as Navigator & { locks?: WebLockManager }).locks;
        if (lockManager) {
            let enteredLock = false;
            try {
                await lockManager.request(`${LockNamespace}:${options.lockScope}`, { mode: 'exclusive' }, async () => {
                    enteredLock = true;
                    if (this.shouldAttempt(options)) await this.performRenewal(options);
                });
                return;
            } catch (err) {
                // Do not replay an operation that ran and threw inside an acquired lock.
                if (enteredLock) throw err;
                logger.warn('Web Locks JWT renewal coordination failed; falling back to storage lease', err);
            }
        }
        await this.renewWithStorageLease(options);
    }

    private shouldAttempt(options: RenewalOptions) {
        const failed = this.failedAttempt;
        return !(failed?.scope === options.scope && failed.until > Date.now()) && options.shouldRenew();
    }

    private async performRenewal(options: RenewalOptions) {
        const result = await options.performRenewal();
        if (result.status === 'failed') this.failedAttempt = { scope: options.scope, until: Date.now() + FailedAttemptCooldownMs };
        // Never broadcast JWTs, clear messages, or failure state to sibling tabs.
    }

    private async renewWithStorageLease(options: RenewalOptions) {
        for (let attempt = 0; attempt < MaxLeaseAttempts; attempt++) {
            if (!this.shouldAttempt(options)) return;
            const acquired = await this.tryAcquireLease(options.lockScope);
            if (acquired) {
                const heartbeat = window.setInterval(() => this.refreshLease(options.lockScope), Math.floor(LeaseMs / 3));
                try {
                    if (this.shouldAttempt(options)) await this.performRenewal(options);
                    return;
                } finally {
                    window.clearInterval(heartbeat);
                    this.releaseLease(options.lockScope);
                }
            }
            // Another tab has its own result scope. Wait for its cookie lock, not a credential
            // broadcast. The next attempt can then adopt the fresh original-account seed.
            const deadline = Date.now() + LeaseMs + LeaseWaitBufferMs;
            while (this.hasActiveLease(options.lockScope) && Date.now() < deadline) {
                if (!this.shouldAttempt(options)) return;
                await delay(LeaseWaitBufferMs);
            }
        }
        if (!this.shouldAttempt(options) || this.hasActiveLease(options.lockScope)) return;
        // Storage may be unavailable; backend row locks and fresh proof checks remain authoritative.
        logger.warn('JWT renewal lease could not be acquired; renewing without a lease');
        await this.performRenewal(options);
    }

    private async tryAcquireLease(scope: string) {
        const lease = this.readLease(scope);
        if (lease && lease.expiresAt > Date.now() && lease.ownerId !== this.ownerId) return false;
        try {
            localStorage.setItem(this.leaseKey(scope), JSON.stringify({ ownerId: this.ownerId, expiresAt: Date.now() + LeaseMs }));
            await delay(LeaseSettleMs);
            return this.readLease(scope)?.ownerId === this.ownerId;
        } catch {
            return false;
        }
    }

    private refreshLease(scope: string) {
        try {
            if (this.readLease(scope)?.ownerId !== this.ownerId) return;
            localStorage.setItem(this.leaseKey(scope), JSON.stringify({ ownerId: this.ownerId, expiresAt: Date.now() + LeaseMs }));
        } catch {
            /* A suspended/failed lease never substitutes for backend authorization. */
        }
    }
    private releaseLease(scope: string) {
        try {
            if (this.readLease(scope)?.ownerId === this.ownerId) localStorage.removeItem(this.leaseKey(scope));
        } catch {
            /* Expiration bounds abandoned leases. */
        }
    }
    private hasActiveLease(scope: string) {
        const lease = this.readLease(scope);
        return !!lease && lease.expiresAt > Date.now();
    }
    private readLease(scope: string): JwtRenewalLease | null {
        try {
            const raw = localStorage.getItem(this.leaseKey(scope));
            if (!raw) return null;
            const lease = JSON.parse(raw) as Partial<JwtRenewalLease>;
            if (typeof lease.ownerId !== 'string' || typeof lease.expiresAt !== 'number' || !Number.isFinite(lease.expiresAt)) return null;
            return { ownerId: lease.ownerId, expiresAt: lease.expiresAt };
        } catch {
            return null;
        }
    }
    private leaseKey(scope: string) {
        return `${StorageLeasePrefix}${scope}`;
    }
}

export const JwtRenewalCoordinator = new JwtRenewalCoordinatorService();
